/* eslint-disable no-undef */
/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 */
import React from 'react';
import { connect, shallowEqual, useSelector } from 'react-redux';
import isNil from 'lodash/isNil';
import {
  hideMissionOverlayMissionListModal,
  closeDetailsPanel,
  closePlatformListDrawer,
  hideTerminalsInYourBeam,
  hideTerminalsInYourBeamPanel,
  openDetailsPanel,
  openPlatformListDrawer,
} from '../../store/ui/actions';
import { clearMission, setMapHidden, FLEET_MAP_ID } from '../../store/maps/actions';
import { clearMissionData } from '../../store/flightLegs/actions';
import {
  cancelRequestFlightPath,
  cancelRequestTailDetailsPanelTerminal,
  cancelRequestTerminalsPoll,
  getTerminals,
} from '../../store/terminals/actions';
import { cancelSteerableBeamsPoll, requestSteerableBeams } from '../../store/steerableBeams/actions';
import { RootState, selectIsRequestingPostServiceTierRequest } from '../../store';
import TailDetailsPanel from './TailDetailsPanel/TailDetailsPanel';
import FlightPathSnackbarContainer from './FlightPath/FlightPathSnackbarContainer';
import { requestAntennas } from '../../store/antennas/actions';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../utils/FeatureFlagUtils';
import { useEffect } from 'react';
import { PlatformListContainer } from './PlatformListContainer';
import ErrorBoundary from '../common/layout/ErrorBoundary';
import ModalLoadingSpinner from '../common/layout/ModalLoadingSpinner';
import { Box } from '@mui/material';

export const LEGACY_DRAWER_WIDTH_PX = 379;
export const KU_PLUS_KA_DRAWER_WIDTH_PX = 439;
export const COLLAPSED_DRAWER_WIDTH_PX = 32;

export interface FleetMapProps extends StateProps, DispatchProps {}

function FleetMap(props: FleetMapProps) {
  const {
    getTerminals,
    antennas,
    requestAntennas,
    hideTerminalsInYourBeamPanel,
    hideMissionOverlayMissionListModal,
    clearMission,
    clearMissionData,
    appConfig,
    requestSteerableBeams,
    isInitialConfigRequestFinished,
    cancelRequestTerminalsPoll,
    cancelRequestFlightPath,
    cancelRequestTailDetailsPanelTerminal,
    cancelSteerableBeamsPoll,
    detailsPanelOpen,
    selectedDatapathUuidFleetView,
    closeDetailsPanel,
    closePlatformListDrawer,
    setMapHidden,
  } = props;

  const isRequestingPostServiceTierRequest = useSelector(selectIsRequestingPostServiceTierRequest, shallowEqual);

  useEffect(() => {
    setMapHidden(false);

    return () => {
      setMapHidden(true);
    };
  }, [setMapHidden]);

  useEffect(() => {
    getTerminals();

    return () => {
      cancelRequestTerminalsPoll();
      cancelRequestTailDetailsPanelTerminal();
      cancelRequestFlightPath();
    };
  }, [getTerminals, cancelRequestTerminalsPoll, cancelRequestTailDetailsPanelTerminal, cancelRequestFlightPath]);

  useEffect(() => {
    if (!antennas || Object.keys(antennas).length === 0) {
      requestAntennas();
    }
  }, [antennas, requestAntennas]);

  useEffect(() => {
    if (isInitialConfigRequestFinished && isFeatureEnabled(appConfig, FEATURE_FLAGS.isHCXEnabled)) {
      requestSteerableBeams();
    }

    return () => {
      if (isFeatureEnabled(appConfig, FEATURE_FLAGS.isHCXEnabled)) {
        cancelSteerableBeamsPoll();
      }
    };
  }, [appConfig, isInitialConfigRequestFinished, requestSteerableBeams, cancelSteerableBeamsPoll]);

  hideMissionOverlayMissionListModal();
  clearMission();
  clearMissionData();

  // When the fleet map container loads, we want to hide the aircraft in your beam panel and remove the selected ids
  // TODO: This will need to be done when navigating to any route that is not the FTV
  hideTerminalsInYourBeamPanel();

  return (
    <React.Fragment>
      <ErrorBoundary
        message="Unable to render the Platform List Container"
        onReset={() => {
          closePlatformListDrawer();
        }}
      >
        <PlatformListContainer />
      </ErrorBoundary>
      <ErrorBoundary
        message="Unable to render the Terminal Details Panel"
        onReset={() => {
          closeDetailsPanel();
        }}
      >
        {detailsPanelOpen && !isNil(selectedDatapathUuidFleetView) ? <TailDetailsPanel /> : null}
      </ErrorBoundary>
      <FlightPathSnackbarContainer />
      {isRequestingPostServiceTierRequest ? (
        <Box sx={{ position: 'absolute', width: '100vw', height: '100vh', zIndex: 9999 }}>
          <ModalLoadingSpinner></ModalLoadingSpinner>
        </Box>
      ) : (
        <></>
      )}
    </React.Fragment>
  );
}

const mapStateToProps = (state: RootState) => ({
  detailsPanelOpen: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].detailsPanelOpen,
  requestTailDetailsPanelTerminalError: state.terminals.requestTailDetailsPanelTerminalError,
  tailDetailsPanelTerminal: state.terminals.tailDetailsPanelTerminal,
  selectedDatapathUuidFleetView:
    state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList,
  isInitialTerminalsRequestFinished: state.terminals.isInitialTerminalsRequestFinished,
  antennas: state.antennas.antennas,
  appConfig: state.config.appConfig,
  isInitialConfigRequestFinished: state.config.isInitialConfigRequestFinished,
  isRequestingSteerableBeams: state.steerableBeams.isRequestingSteerableBeams,
  isInitialSteerableBeamsRequestFinished: state.steerableBeams.isInitialSteerableBeamsRequestFinished,
});
type StateProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = {
  openDetailsPanel,
  closeDetailsPanel,
  openPlatformListDrawer,
  closePlatformListDrawer,
  getTerminals,
  cancelRequestTerminalsPoll,
  hideTerminalsInYourBeamPanel,
  hideTerminalsInYourBeam,
  requestAntennas,
  cancelRequestTailDetailsPanelTerminal,
  cancelRequestFlightPath,
  hideMissionOverlayMissionListModal,
  clearMission: clearMission(FLEET_MAP_ID),
  clearMissionData: clearMissionData(FLEET_MAP_ID),
  requestSteerableBeams,
  cancelSteerableBeamsPoll,
  setMapHidden: setMapHidden(FLEET_MAP_ID),
};
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(FleetMap);
